import * as React from 'react';

/**
 * Where the longest name in the ledger ends, so the File column can start
 * right after it (`_columns.scss`, `file-fill`) and the spare width falls
 * after the file names instead of between the names and the files.
 *
 * Measured off the rows themselves: a row's name starts after its depth's
 * indent, its glyph and its id, and carries tags after it, none of which the
 * tree's data says the width of.
 */

/** How far into a row's content box its name block reaches, cut-off text included. */
export const nameExtentOf = (row: HTMLElement): number => {
  const main = row.querySelector<HTMLElement>('.tree-label-main');
  if (!main) return 0;

  const rowStyle = getComputedStyle(row);
  const rtl = rowStyle.direction === 'rtl';
  const rowBox = row.getBoundingClientRect();
  const contentStart = rtl
    ? rowBox.right - (parseFloat(rowStyle.paddingInlineStart) || 0)
    : rowBox.left + (parseFloat(rowStyle.paddingInlineStart) || 0);

  // The far edge of whatever the block shows: its id, name and tags.
  let end = 0;
  Array.from(main.children).forEach((child) => {
    const box = child.getBoundingClientRect();
    if (!box.width) return;
    end = Math.max(
      end,
      rtl ? contentStart - box.left : box.right - contentStart
    );
  });
  if (!end) return 0;

  // A name already cut short is as long as its text, not its box.
  let hidden = 0;
  main
    .querySelectorAll<HTMLElement>('.tree-label-text, .tree-label-name__text')
    .forEach((el) => {
      hidden = Math.max(hidden, el.scrollWidth - el.clientWidth);
    });

  const gutter = parseFloat(getComputedStyle(main).paddingInlineEnd) || 0;
  return end + hidden + gutter;
};

/** The longest name extent among the rows under `root`. */
export const nameColumnExtent = (root: HTMLElement): number => {
  let extent = 0;
  root.querySelectorAll<HTMLElement>('.tree-ledger-row').forEach((row) => {
    extent = Math.max(extent, nameExtentOf(row));
  });
  return Math.ceil(extent);
};

/**
 * Keeps `--tree-ledger-name-extent` on the explorer column up to date as rows
 * open, close, rename and the explorer resizes. Until it is set, the File
 * column keeps its usual width.
 */
export const useNameColumnExtent = (
  ref: React.RefObject<HTMLElement | null>
): void => {
  React.useLayoutEffect(() => {
    const root = ref.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;

    let frame = 0;
    let current = '';
    const measure = () => {
      frame = 0;
      const extent = nameColumnExtent(root);
      const next = extent ? `${extent}px` : '';
      if (next === current) return;
      current = next;
      if (next) root.style.setProperty('--tree-ledger-name-extent', next);
      else root.style.removeProperty('--tree-ledger-name-extent');
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };

    // Not the root's own attributes: setting the property would observe itself.
    const mutations = new MutationObserver(schedule);
    mutations.observe(root, {
      childList: true,
      subtree: true,
      characterData: true,
    });
    const resizes = new ResizeObserver(schedule);
    resizes.observe(root);
    document.fonts?.ready.then(schedule);
    schedule();

    return () => {
      mutations.disconnect();
      resizes.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [ref]);
};
